import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { renderDisplayTool, isDisplayTool, pendingLabelFor, PresentationDownloadCard } from '@usalco/catalyst-component-kit';
import { ReservoirChatContext } from './ReservoirChatContext';
import ArtifactCard from './ArtifactCard';
import { TOOL_PROPOSE_REPORT_EDIT, TOOL_GENERATE_PRESENTATION, TOOL_ADD_VISUAL_COMPONENT, CHAT_KIND_BUILDER, isArtifactToolPart } from './constants';

// Routes a streamed tool part to inline UI. Display tools echo their args as the
// result, so we prefer `result` once present and fall back to `args` mid-stream.
export default function ToolResultRenderer({ part }) {
  const navigate = useNavigate();
  const { context, builderApply } = React.useContext(ReservoirChatContext);
  const [applied, setApplied] = React.useState(false);
  const payload = part.state === 'result' ? structuredClone(part.result) : part.args;

  // Self-describing display tools (show_chart/table/gauge/…) render via the shared
  // @usalco/catalyst-component-kit registry — one implementation, identical to Catalyst's chat.
  if (isDisplayTool(part.toolName)) {
    if (part.state !== 'result') return <Pending label={`Generating ${pendingLabelFor(part.toolName)}…`} />;
    return renderDisplayTool(part.toolName, payload);
  }

  if (part.toolName === TOOL_PROPOSE_REPORT_EDIT) {
    const p = payload || {};
    // A proposal the gateway rejected (failed server-side v4 schema validation) comes back as a
    // terminal result with no `definition`. The model self-corrects and emits a fresh proposal, so
    // render nothing for the rejected one rather than a dead, permanently-disabled Apply card.
    if (part.state === 'result' && !p.definition) return null;
    const ready = part.state === 'result' && !!p.definition;
    // In the builder we apply in place (the builder registered builderApply); elsewhere we
    // deep-link into the builder with the proposal in router state.
    const inBuilder = context?.kind === CHAT_KIND_BUILDER && typeof builderApply === 'function';
    return (
      <Paper variant="outlined" sx={{ my: 1, p: 1.5, borderColor: 'primary.main', bgcolor: 'action.hover' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <AutoFixHighIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2">Proposed report change</Typography>
        </Box>
        {p.summary ? <Typography variant="body2" sx={{ mb: 1 }}>{p.summary}</Typography> : null}
        {inBuilder ? (
          <Button
            variant="contained"
            size="small"
            disabled={!ready || applied}
            onClick={() => { builderApply(p.definition, p.summary); setApplied(true); }}
          >
            {applied ? 'Applied' : 'Apply to builder'}
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            disabled={!ready}
            onClick={() => navigate('/report4/builder', {
              state: { draftDefinition: p.definition, reportID: p.reportID },
            })}
          >
            Review in builder
          </Button>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {inBuilder
            ? 'Applies to the canvas only — nothing is saved until you Save.'
            : 'Nothing is saved until you apply it in the builder.'}
        </Typography>
      </Paper>
    );
  }

  if (part.toolName === TOOL_ADD_VISUAL_COMPONENT) {
    const p = payload || {};
    // A rejected component (gateway validation failed) has no `component`.
    if (part.state === 'result' && !p.component) return null;
    const ready = part.state === 'result' && !!p.component;
    const inBuilder = context?.kind === CHAT_KIND_BUILDER && typeof builderApply === 'function';
    const handleAdd = () => {
      const def = context?.working_definition;
      if (!def || !p.component) return;
      const pages = def.pages ?? [];
      const pageIdx = p.page_lid ? pages.findIndex((pg) => pg.lid === p.page_lid) : pages.length - 1;
      const idx = pageIdx >= 0 ? pageIdx : pages.length - 1;
      if (idx < 0) return;
      const updated = {
        ...def,
        pages: def.pages.map((pg, i) =>
          i === idx ? { ...pg, components: [...(pg.components ?? []), p.component] } : pg
        ),
      };
      builderApply(updated, p.summary);
      setApplied(true);
    };
    return (
      <Paper variant="outlined" sx={{ my: 1, p: 1.5, borderColor: 'primary.main', bgcolor: 'action.hover' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <AutoFixHighIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2">Add visual component</Typography>
        </Box>
        {p.summary ? <Typography variant="body2" sx={{ mb: 1 }}>{p.summary}</Typography> : null}
        {inBuilder ? (
          <Button variant="contained" size="small" disabled={!ready || applied} onClick={handleAdd}>
            {applied ? 'Added' : 'Add to report'}
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">Open the report builder to add this component.</Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Appended to the canvas — nothing is saved until you Save.
        </Typography>
      </Paper>
    );
  }

  if (part.toolName === TOOL_GENERATE_PRESENTATION) {
    if (part.state !== 'result' || !payload?.base64) return <Pending label="Building presentation…" />;
    return <PresentationDownloadCard result={payload} />;
  }

  // Catalyst artifacts (freeform HTML/JS/SVG/markdown mockups): render the sandboxed card. It reads
  // args mid-stream and the result once it lands, so no separate Pending state here.
  if (isArtifactToolPart(part)) {
    return <ArtifactCard part={part} />;
  }

  // MCP data tools (query/list_tables/…): show a subtle activity chip; the model
  // turns the data into a show_table/show_chart, which renders above.
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={part.state === 'result' ? undefined : <CircularProgress size={12} />}
      label={part.state === 'result' ? `ran ${part.toolName}` : `running ${part.toolName}…`}
      sx={{ my: 0.5, opacity: 0.7 }}
    />
  );
}

function Pending({ label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, color: 'text.secondary' }}>
      <CircularProgress size={14} />
      <Typography variant="caption">{label}</Typography>
    </Box>
  );
}
